import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import moment from 'moment';
import { appointmentService, bookingService } from '../../services/api';

const AthleteScheduleSection = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMode, setFilterMode] = useState('upcoming'); // upcoming | all | past

  const loadAppointments = async () => {
    setLoading(true);
    setError('');
    try {
      const [appRes, bookRes] = await Promise.allSettled([
        appointmentService.getAppointments(),
        bookingService.getMyBookings()
      ]);

      let combined = [];

      // 1. Appuntamenti e lezioni assegnate
      if (appRes.status === 'fulfilled' && appRes.value?.success && appRes.value.data) {
        const rawApps = Array.isArray(appRes.value.data)
          ? appRes.value.data
          : (appRes.value.data?.items || []);
        combined = combined.concat(rawApps.map((a) => ({
          ...a,
          isBooking: false
        })));
      }

      // 2. Prenotazioni autonome degli slot (Booking)
      if (bookRes.status === 'fulfilled' && bookRes.value?.success && bookRes.value.data) {
        const rawBookings = Array.isArray(bookRes.value.data)
          ? bookRes.value.data
          : (bookRes.value.data?.items || []);

        const mappedBookings = rawBookings.map((b) => {
          const startIso = (b.date && b.startTime) ? `${b.date}T${b.startTime}:00` : b.date;
          const endIso = (b.date && b.endTime) ? `${b.date}T${b.endTime}:00` : startIso;
          return {
            id: b.id,
            title: b.title || b.courseName || 'Lezione Prenotata',
            courseName: b.courseName,
            groupName: null,
            startTime: startIso,
            endTime: endIso,
            locationName: b.locationName || 'N/D',
            coachName: b.coachName || 'N/D',
            status: b.status === 'confirmed' ? 'scheduled' : b.status,
            rawStatus: b.status,
            isBooking: true,
            canCancel: b.canCancel,
            bookingSlotId: b.bookingSlotId
          };
        });

        combined = combined.concat(mappedBookings);
      }

      // Se entrambi i servizi hanno riscontrato errori
      if (
        (appRes.status === 'rejected' || !appRes.value?.success) &&
        (bookRes.status === 'rejected' || !bookRes.value?.success)
      ) {
        const errText = appRes.value?.message || bookRes.value?.message || 'Errore nel caricamento delle lezioni e prenotazioni';
        setError(typeof errText === 'string' ? errText : (errText?.message || 'Errore nel caricamento delle lezioni'));
      }

      // Ordinamento cronologico
      combined.sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf());
      setAppointments(combined);
    } catch (err) {
      console.error('Error loading schedule and bookings:', err);
      setError('Errore durante il caricamento del calendario lezioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  const now = moment();
  const filteredAppointments = appointments.filter((app) => {
    const end = moment(app.endTime);
    if (filterMode === 'upcoming') {
      return end.isSameOrAfter(now);
    }
    if (filterMode === 'past') {
      return end.isBefore(now);
    }
    return true;
  });

  return (
    <Card className="mb-4 shadow-sm border-0" data-testid="athlete-schedule-section">
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-bold">
          <i className="fas fa-calendar-check me-2"></i>
          Le Mie Lezioni & Appuntamenti (Sola Lettura)
        </h5>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm">
            <Button
              variant={filterMode === 'upcoming' ? 'light' : 'outline-light'}
              onClick={() => setFilterMode('upcoming')}
              data-testid="filter-upcoming-btn"
            >
              Prossime
            </Button>
            <Button
              variant={filterMode === 'past' ? 'light' : 'outline-light'}
              onClick={() => setFilterMode('past')}
              data-testid="filter-past-btn"
            >
              Passate
            </Button>
            <Button
              variant={filterMode === 'all' ? 'light' : 'outline-light'}
              onClick={() => setFilterMode('all')}
              data-testid="filter-all-btn"
            >
              Tutte
            </Button>
          </div>
          <Button
            variant="outline-light"
            size="sm"
            onClick={loadAppointments}
            disabled={loading}
            title="Aggiorna calendario"
            data-testid="refresh-schedule-btn"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <span className="ms-2 text-muted small">Caricamento lezioni in programma...</span>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="far fa-calendar-times fa-2x mb-2 d-block text-secondary"></i>
            Nessuna lezione trovata per i criteri selezionati.
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light small">
                <tr>
                  <th>Data & Ora</th>
                  <th>Lezione</th>
                  <th>Stanza / Location</th>
                  <th>Coach</th>
                  <th className="text-center">Stato</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.map((app) => {
                  let statusBadge = <Badge bg="primary">In Programma</Badge>;
                  if (app.status === 'completed') statusBadge = <Badge bg="success">Svolta</Badge>;
                  if (app.status === 'cancelled') statusBadge = <Badge bg="secondary">Annullata</Badge>;
                  if (app.rawStatus === 'confirmed' || app.status === 'confirmed') {
                    statusBadge = (
                      <Badge bg="success">
                        <i className="fas fa-check-circle me-1"></i>Confermata
                      </Badge>
                    );
                  }

                  return (
                    <tr key={app.id} data-testid={`schedule-item-${app.id}`}>
                      <td>
                        <div className="fw-semibold text-dark">
                          {moment(app.startTime).format('dddd DD MMMM YYYY')}
                        </div>
                        <small className="text-muted">
                          {moment(app.startTime).format('HH:mm')} - {moment(app.endTime).format('HH:mm')}
                        </small>
                      </td>
                      <td className="fw-bold text-primary">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span>{app.title}</span>
                          {app.isBooking && (
                            <Badge bg="info-subtle" text="info-emphasis" className="border border-info-subtle small fw-normal">
                              <i className="fas fa-bookmark me-1"></i> Prenotazione
                            </Badge>
                          )}
                        </div>
                        {(app.groupName || app.courseName) && (
                          <small className="d-block text-muted fw-normal">
                            {app.groupName ? `Gruppo: ${app.groupName}` : `Corso: ${app.courseName}`}
                          </small>
                        )}
                      </td>
                      <td>
                        <i className="fas fa-map-marker-alt text-danger me-1"></i>
                        {app.locationName}
                      </td>
                      <td>
                        <i className="fas fa-user-ninja text-secondary me-1"></i>
                        {app.coachName}
                      </td>
                      <td className="text-center">
                        {statusBadge}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AthleteScheduleSection;
